import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@material-ui/core';
import { Element } from 'react-scroll';
import SearchIcon from '@material-ui/icons/Search';
import SettingsIcon from '@material-ui/icons/Settings';

import Select from '../components/Select';
import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';

import EventItem from '../components/EventItem';
import BirthdateItem from '../components/BirthdateItem';
import TodoItem from '../components/TodoItem';

import useDiaryData from '../hooks/useDiaryData';
import useMainContext from '../hooks/useMainContext';
import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';

import diary from '../api/diary';
import { maxWidth } from '../components/configs';
import { getDiaryYearOptions } from '../utils';

import { eventStatusOptions } from '../configs/events';

const queryParamKey = 'search-diary';
const dateDisplayFormat = 'DD MMM YYYY';
const emptyValue = 'NONE';
const yearOptions = getDiaryYearOptions();

const generateDefaultState = () => {
  return {
    searchKeyword: '',
    keyword: '',
    loaded: false,
    loading: false,
    data: [],
    timestamp: moment().format('x'),
    filter: {
      events: true,
      birthdates: true,
      todoes: true,
    },
  };
};

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth,
    width: '100%',
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  keywordWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 2,
  },
  textField: {
    width: 'calc(100% - 44px)',
  },
  date: {
    position: 'sticky',
    top: 74,
    borderBottom: `1px solid #e0e0e0`,
    backgroundColor: 'white',
    fontSize: 18,
    fontWeight: 600,
    paddingTop: 10,
    paddingBottom: 5,
    //marginBottom: 5,
    zIndex: 1,
    paddingLeft: 10,
  },
  innerDate: {
    fontSize: 16,
    fontWeight: 300,
  },
  listWrapper: {
    backgroundColor: '#f9f9f9',
    //borderTop: `1px solid #e0e0e0`,
    [theme.breakpoints.up('sm')]: {
      borderLeft: `1px solid #e0e0e0`,
      borderRight: `1px solid #e0e0e0`,
    },
  },
  noFound: {
    color: '#9c9c9c',
    fontWeight: 300,
    fontSize: 13,
    padding: 10,
    borderBottom: `1px solid #e0e0e0`,
  },
}));

function SearchDialog() {
  const classes = useStyles();
  // eslint-disable-next-line
  const [_, __, handleSetDate, handleManualFetch] = useDiaryData();
  const [queryParams] = useQueryParams();
  const [state, _setState] = useState(generateDefaultState());
  const handleSnackbarChange = useSnackbarText();
  const [anchorEl, setAnchorEl] = useState(null);
  const { handleChange: handleMainContextChange } = useMainContext();
  const [statusFilter, setStatusFilter] = useState(emptyValue);
  const [yearFilter, setYearFilter] = useState(emptyValue);

  const handleStatusFilterChange = (event, value) => {
    setStatusFilter(value);
  };

  const handleYearFilterChange = (event, value) => {
    setYearFilter(value);
  };

  const handleOpenMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const queryValue = queryParams[queryParamKey];

  const setState = useCallback(newState => {
    _setState(prevState => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  const handleChange = type => (event, value) => {
    if (value !== undefined) setState({ [type]: value });
    else setState({ [type]: event.target.value });
  };

  const handleSearch = useCallback(async () => {
    const keyword = state.keyword.trim();

    if (keyword.length < 3) {
      handleSnackbarChange({
        text: 'Keyword is too short, at least 3 characters',
        variant: 'error',
      });
      return;
    }

    setState({
      loading: true,
      searchKeyword: keyword,
    });

    const { data, success } = await diary.getDiaryByKeyword({ keyword });

    if (!success) {
      return handleSnackbarChange({ text: data.message, variant: 'error' });
    } else {
      setState({
        data: data.diary,
        loaded: true,
        loading: false,
        timestamp: moment().format('x'),
      });
    }
    // eslint-disable-next-line
  }, [state.data, state.keyword, setState, handleSnackbarChange]);

  const handleSetFilter = type => () => {
    const currentValue = state.filter;
    setState({
      filter: {
        ...currentValue,
        [type]: !currentValue[type],
      },
    });
  };

  useEffect(() => {
    if (queryValue) {
      handleMainContextChange({ onEventStatusUpdate: handleSearch });
    } else {
      handleMainContextChange({ onEventStatusUpdate: null });
    }
  }, [queryValue, handleMainContextChange, handleSearch]);

  useEffect(() => {
    _setState(generateDefaultState());
    if (!queryValue) {
      handleManualFetch();
    }
  }, [queryValue, handleManualFetch]);

  const formattedData = useMemo(() => {
    return state.data
      .map(e => ({
        ...e,
        events: state.filter.events ? e.events : [],
        birthdates: state.filter.birthdates ? e.birthdates : [],
        todoes: state.filter.todoes ? e.todoes : [],
      }))
      .filter(e =>
        Boolean(e.events.length || e.birthdates.length || e.todoes.length)
      )
      .filter(e => {
        if (statusFilter === emptyValue) return true;

        const eventsWithStatusFilter = e.events.filter(event =>
          event.status.split(',').includes(statusFilter)
        );

        e.events = eventsWithStatusFilter;
        return eventsWithStatusFilter.length > 0;
      })
      .filter(e => {
        if (yearFilter === emptyValue) return true;
        return moment(e.date).year() === Number(yearFilter);
      });
  }, [
    state.data,
    state.filter.events,
    state.filter.birthdates,
    state.filter.todoes,
    statusFilter,
    yearFilter,
  ]);

  /*
	const formattedData = state.data.map(e => ({
		...e,
		events: state.filter.events ? e.events : [],
		birthdates: state.filter.birthdates ? e.birthdates : [],
		todoes: state.filter.todoes ? e.todoes : [],
	})).filter(e => Boolean(e.events.length || e.birthdates.length || e.todoes.length));
	*/

  return (
    <FullScreenDialog
      title={`Search diary ${
        state.searchKeyword ? '"' + state.searchKeyword + '"' : ''
      }`}
      queryParamKey={queryParamKey}
    >
      <div className={classes.container}>
        <div className={classes.keywordWrapper}>
          <TextField
            className={classes.textField}
            label="Keyword"
            placeholder="Event name, birthdate name, to do list title"
            value={state.keyword}
            onChange={handleChange('keyword')}
            onEnterPress={handleSearch}
            helperText="(At least 3 characters)"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={handleOpenMenu} style={{ marginTop: 8 }}>
            <SettingsIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={handleSetFilter('events')}>
              <Checkbox checked={state.filter.events} size="small" />
              <ListItemText primary="Events" />
            </MenuItem>
            <MenuItem onClick={handleSetFilter('birthdates')}>
              <Checkbox checked={state.filter.birthdates} size="small" />
              <ListItemText primary="Birthdates" />
            </MenuItem>
            <MenuItem onClick={handleSetFilter('todoes')}>
              <Checkbox checked={state.filter.todoes} size="small" />
              <ListItemText primary="To do list" />
            </MenuItem>
          </Menu>
        </div>

        <Select
          label="Event Status"
          options={[
            { label: 'None', value: emptyValue },
            // disable `Non Face To Face`
            ...eventStatusOptions.filter(option => option.value !== '12'),
          ]}
          value={statusFilter}
          onChange={handleStatusFilterChange}
        />

        <Select
          label="Year"
          options={[
            { label: 'None', value: emptyValue },
            ...yearOptions,
          ]}
          value={yearFilter}
          onChange={handleYearFilterChange}
        />

        {state.loading && (
          <Typography className={classes.noFound}>Loading</Typography>
        )}
        {state.loaded && !Boolean(formattedData.length) && (
          <Typography className={classes.noFound}>No found</Typography>
        )}
        {formattedData.map((e, i) => (
          <Element key={e.date} name={e.date}>
            <Typography className={classes.date}>
              {`${moment(e.date).format('ddd')} `}
              <span className={classes.innerDate}>
                {moment(e.date).format(dateDisplayFormat)}
              </span>
            </Typography>
            <div className={classes.listWrapper}>
              {e.birthdates.map((el, j) => (
                <BirthdateItem key={j} details={el} />
              ))}

              {e.events.map((el, j) => (
                <EventItem key={j} details={el} />
              ))}

              {e.todoes.map((el, j) => (
                <TodoItem key={j} details={el} sortableEnabled={false} />
              ))}
            </div>
          </Element>
        ))}
      </div>
    </FullScreenDialog>
  );
}

export default SearchDialog;
